import React, {useEffect, useRef, useState} from 'react';
import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import {COLORS} from '@/utils/constants';
import strSlug from '@/utils/strSlug';

interface Props {
	final: any[];
}

const CATEGORIES = [
	{
		name: 'Family & friends',
		question: 'question49',
	},
	{
		name: 'Business Angels',
		question: 'question54',
	},
	{
		name: 'Crowdfunding',
		question: 'question59',
	},
	{
		name: 'Incubators and Accelerators',
		question: 'question64',
	},
	{
		name: 'NGO’s loans',
		question: 'question69',
	},
	{
		name: 'NGO’s grants',
		question: 'question74',
	},
	{
		name: 'Public subsidies',
		question: 'question79',
	},
	{
		name: 'Public grants',
		question: 'question84',
	},
	{
		name: 'Venture capital',
		question: 'question89',
	},
	{
		name: 'Microfinance',
		question: 'question94',
	},
	{
		name: 'Financial institutions (banks)',
		question: 'question99',
	},
	{
		name: 'Other',
		question: 'question104',
	},
];

const EntrepreneursObtainedFunds = ({final}: Props) => {
	const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
	const [options, setOptions] = useState<Highcharts.Options>();

	const toPercent = (value: number, total: number) => {
		if (!total) return 0;

		return Number(((value / total) * 100).toFixed(1));
	};

	const calculateSubmissions = () => {
		const data: {[index: string]: {[index: string]: number}} = {
			yes: {},
			pending: {},
			no: {},
		};

		CATEGORIES.forEach((category) => {
			data.yes[strSlug(category.question)] = 0;
			data.pending[strSlug(category.question)] = 0;
			data.no[strSlug(category.question)] = 0;
		})

		final.forEach((submission: any) => {
			CATEGORIES.forEach((category) => {
				const answer = submission[category.question];

				if (answer === 'Item 1') {
					data.yes[strSlug(category.question)] = (data.yes[strSlug(category.question)] || 0) + 1;
				} else if (answer === 'Item 2') {
					data.pending[strSlug(category.question)] = (data.pending[strSlug(category.question)] || 0) + 1;
				} else if (answer === 'Item 3') {
					data.no[strSlug(category.question)] = (data.no[strSlug(category.question)] || 0) + 1;
				}
			})
		})

		const yesData = CATEGORIES.map((category) => {
			const key = strSlug(category.question);
			const total =
				data.yes[key] +
				data.pending[key] +
				data.no[key];

			return toPercent(data.yes[key], total);
		});

		const pendingData = CATEGORIES.map((category) => {
			const key = strSlug(category.question);
			const total =
				data.yes[key] +
				data.pending[key] +
				data.no[key];

			return toPercent(data.pending[key], total);
		});

		const noData = CATEGORIES.map((category) => {
			const key = strSlug(category.question);
			const total =
				data.yes[key] +
				data.pending[key] +
				data.no[key];

			return toPercent(data.no[key], total);
		});

		const dashboardOptions: Highcharts.Options = {
			chart: {
				type: 'bar',
			},
			title: {
				text: '% of entrepreneurs who obtained the funds by type of fund',
				align: 'left',
			},
			xAxis: {
				categories: CATEGORIES.map((category) => category.name),
			},
			yAxis: {
				title: {
					text: null,
				},
			},
			legend: {
				verticalAlign: 'bottom',
			},
			plotOptions: {
				bar: {
					stacking: 'percent',
					dataLabels: {
						enabled: true,
						format: '{point.percentage:.1f}%'
					},
				},
			},
			series: [
				{
					type: 'bar',
					name: 'No',
					data: noData,
					color: COLORS.gray
				},
				{
					type: 'bar',
					name: 'Pending',
					data: pendingData,
					color: COLORS.orange
				},
				{
					type: 'bar',
					name: 'Yes',
					data: yesData,
					color: COLORS.green,
				},
			],
		};

		setOptions(dashboardOptions);
	};

	useEffect(() => {
		calculateSubmissions();
	}, []);

	return (
		<Card className='chart-container'>
			<CardContent>
				{options && (
					<HighchartsReact
						highcharts={Highcharts}
						options={options}
						ref={chartComponentRef}
					/>
				)}
			</CardContent>
		</Card>
	);
};

export default EntrepreneursObtainedFunds;
